import React, { useState, useEffect } from 'react';
import { Button, ButtonGroup, Switch, FormControlLabel, Alert } from '@mui/material';
import {
  ThemeData,
  getUserThemePreferences,
  togglePersonalTheme,
  savePersonalTheme,
  uploadPersonalBackground,
  getTheme
} from '../../api/userApi';
import { compressBackgroundImage } from '../../utils/imageCompression';

const defaultTheme: ThemeData = {
  colors: {
    primary: '#3a9397',
    primaryLight: '#00c2cc',
    primaryDark: '#000000',
    secondary: 'rgba(0, 0, 0, 0.75)',
    secondaryLight: '#919191',
    secondaryDark: '#9a0036',
    backgroundDefault: 'rgba(0, 0, 0, 0.43)',
    backgroundPaper: 'rgba(102, 70, 0, 0.26)',
    textPrimary: '#ffffff',
    textSecondary: 'rgba(245, 190, 86, 0.9)',
    error: '#f44336',
    warning: '#ff9800',
    info: '#ffffff',
    success: '#4caf50',
    borderColor: '#e0e0e0',
    authFieldBackground: 'rgba(255, 255, 255, 0.1)',
  },
  typography: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
    fontSize: '14px',
    h1Size: '2.125rem',
    h2Size: '1.5rem',
    h3Size: '1.25rem',
  },
  spacing: {
    unit: '8px',
    containerPadding: '16px',
    cardMargin: '16px',
  },
  layout: {
    borderRadius: '4px',
    borderRadiusSm: '2px',
    borderRadiusLg: '8px',
  },
  effects: {
    shadowLight: '0 1px 3px rgba(0,0,0,0.12)',
    shadowMedium: '0 4px 6px rgba(0,0,0,0.16)',
    shadowHeavy: '0 10px 20px rgba(0,0,0,0.19)',
    glassmorphism: false,
    darkMode: false,
    highContrast: false,
  }
};

export const UserThemeCustomizer: React.FC = () => {
  const [usePersonalTheme, setUsePersonalTheme] = useState(false);
  const [theme, setTheme] = useState<ThemeData>(defaultTheme);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeSection, setActiveSection] = useState('toggle');

  useEffect(() => {
    loadPreferences();
  }, []);

  useEffect(() => {
    if (usePersonalTheme && theme) {
      applyThemeToCSS(theme);
    } else {
      // Load app-wide theme
      loadAppWideTheme();
    }
  }, [usePersonalTheme, theme]);

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const response = await getUserThemePreferences();
      if (response.data?.data) {
        setUsePersonalTheme(response.data.data.usePersonalTheme);
        setBackgroundUrl(response.data.data.personalBackgroundUrl);

        if (response.data.data.personalTheme) {
          setTheme(mergeWithDefaults(response.data.data.personalTheme));
        }
      }
    } catch (error: any) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAppWideTheme = async () => {
    try {
      const response = await getTheme();
      if (response.data?.data?.theme) {
        applyThemeToCSS(response.data.data.theme);
      }
    } catch (error) {
      console.warn('Failed to load app-wide theme:', error);
    }
  };

  const mergeWithDefaults = (loadedTheme: any): ThemeData => {
    return {
      colors: { ...defaultTheme.colors, ...loadedTheme.colors },
      typography: { ...defaultTheme.typography, ...loadedTheme.typography },
      spacing: { ...defaultTheme.spacing, ...loadedTheme.spacing },
      layout: { ...defaultTheme.layout, ...loadedTheme.layout },
      effects: { ...defaultTheme.effects, ...loadedTheme.effects }
    };
  };

  const applyThemeToCSS = (themeData: ThemeData) => {
    const root = document.documentElement;

    const colorMap: Record<string, string> = {
      'primary': '--primary-color',
      'primaryLight': '--primary-light',
      'primaryDark': '--primary-dark',
      'secondary': '--secondary-color',
      'secondaryLight': '--secondary-light',
      'secondaryDark': '--secondary-dark',
      'backgroundDefault': '--background-default',
      'backgroundPaper': '--background-paper',
      'textPrimary': '--text-primary',
      'textSecondary': '--text-secondary',
      'error': '--error-color',
      'warning': '--warning-color',
      'info': '--info-color',
      'success': '--success-color',
      'borderColor': '--border-color',
      'authFieldBackground': '--auth-field-background'
    };

    Object.entries(themeData.colors).forEach(([key, value]) => {
      const cssVarName = colorMap[key] || `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });

    root.style.setProperty('--font-family', themeData.typography.fontFamily);
    root.style.setProperty('--font-size', themeData.typography.fontSize);
    root.style.setProperty('--border-radius', themeData.layout.borderRadius);
  };

  const handleToggleChange = async (checked: boolean) => {
    try {
      const response = await togglePersonalTheme(checked);
      console.log('Toggle response:', response);

      if (response.data?.success) {
        setUsePersonalTheme(checked);
        setMessage({ type: 'success', text: checked ? 'Switched to personal theme' : 'Switched to app-wide theme' });
        setTimeout(() => setMessage(null), 3000);

        // Reload the page to apply the theme change
        window.location.reload();
      } else {
        throw new Error(response.data?.message || 'Toggle failed');
      }
    } catch (error: any) {
      console.error('Toggle theme error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to toggle theme preference';
      setMessage({ type: 'error', text: errorMsg });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const hexToRgba = (hex: string, alpha: number = 1): string => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;

    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return alpha === 1 ? hex : `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const handleColorChange = (colorKey: keyof ThemeData['colors'], value: string) => {
    setTheme(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorKey]: value
      }
    }));
  };

  const handleOpacityChange = (colorKey: keyof ThemeData['colors'], alpha: number) => {
    const currentColor = theme.colors[colorKey];
    const { hex } = parseColor(currentColor);
    const newColor = hexToRgba(hex, alpha);

    setTheme(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorKey]: newColor
      }
    }));
  };

  const handleSaveTheme = async () => {
    setSaving(true);
    try {
      await savePersonalTheme(theme);
      setMessage({ type: 'success', text: 'Personal theme saved!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to save theme' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setTheme(defaultTheme);
    applyThemeToCSS(defaultTheme);
    setMessage({ type: 'success', text: 'Theme reset to default!' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleDownloadTheme = () => {
    const themeJson = JSON.stringify(theme, null, 2);
    const blob = new Blob([themeJson], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'BROODbook_Personal_Theme.json';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setMessage({ type: 'success', text: 'Theme downloaded!' });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleUploadTheme = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const uploadedTheme = JSON.parse(text);

      // Validate theme structure
      if (!uploadedTheme.colors || !uploadedTheme.typography || !uploadedTheme.spacing || !uploadedTheme.layout || !uploadedTheme.effects) {
        throw new Error('Invalid theme file structure');
      }

      setTheme(uploadedTheme);
      applyThemeToCSS(uploadedTheme);
      setMessage({ type: 'success', text: 'Theme uploaded successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to upload theme. Please check the file format.' });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const handleBackgroundUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Compress the image
      const compressedFile = await compressBackgroundImage(file);

      // Upload
      const response = await uploadPersonalBackground(compressedFile);
      console.log('Upload response:', response);

      // Backend returns { success, message, backgroundUrl } directly in data, not nested
      const newBackgroundUrl = (response.data as any).backgroundUrl;
      console.log('Background URL from response:', newBackgroundUrl);

      if (newBackgroundUrl) {
        console.log('Setting background URL to:', newBackgroundUrl);
        setBackgroundUrl(newBackgroundUrl);

        // Apply background immediately if personal theme is enabled
        if (usePersonalTheme) {
          console.log('Applying background immediately, personal theme is ON');
          const body = document.body;
          body.style.backgroundImage = `url('${newBackgroundUrl}')`;
          body.style.backgroundSize = 'cover';
          body.style.backgroundPosition = 'center center';
          body.style.backgroundAttachment = 'scroll';
          body.style.backgroundRepeat = 'repeat-x';
          body.style.backgroundColor = 'black';
          body.style.animation = 'moveBackground 60s linear infinite';
          console.log('Background applied to body:', body.style.backgroundImage);
        } else {
          console.log('Personal theme is OFF, background not applied immediately');
        }

        setMessage({ type: 'success', text: usePersonalTheme ? 'Background uploaded and applied!' : 'Background uploaded! Enable personal theme to use it.' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        console.error('No backgroundUrl in response:', response);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to upload background' });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setUploading(false);
    }
  };

  const parseColor = (color: string): { hex: string; alpha: number } => {
    if (color.startsWith('rgba(')) {
      const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([01]?\.?\d*)\)/);
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        const alpha = parseFloat(match[4]);
        const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
        return { hex, alpha };
      }
    }
    return { hex: color, alpha: 1 };
  };

  if (loading) {
    return <div className="card-content"><p>Loading preferences...</p></div>;
  }

  return (
    <div className="card-content" style={{ position: 'relative' }}>
      {message && (
        <div
          className={`alert ${message.type === 'error' ? 'alert-error' : 'alert-success'}`}
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            minWidth: '300px',
            maxWidth: '600px',
            margin: 0
          }}
        >
          {message.text}
        </div>
      )}

      <h3 className="mb-3">Personal Theme Settings</h3>

      {/* Theme Toggle */}
      <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: 'var(--background-paper)', borderRadius: '4px' }}>
        <FormControlLabel
          control={
            <Switch
              checked={usePersonalTheme}
              onChange={(e) => handleToggleChange(e.target.checked)}
              color="primary"
            />
          }
          label={usePersonalTheme ? 'Using Personal Theme' : 'Using App-Wide Theme'}
        />
        <p style={{ marginTop: '8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          {usePersonalTheme
            ? 'Your personal theme and background are active. Customize them below.'
            : 'You are using the app-wide theme set by administrators. Toggle to customize your own.'}
        </p>
      </div>

      {usePersonalTheme ? (
        <>
          {/* Tab Navigation */}
          <ButtonGroup variant="contained" sx={{ mb: 3 }}>
            <Button
              variant={activeSection === 'colors' ? 'contained' : 'outlined'}
              onClick={() => setActiveSection('colors')}
            >
              Colors
            </Button>
            <Button
              variant={activeSection === 'background' ? 'contained' : 'outlined'}
              onClick={() => setActiveSection('background')}
            >
              Background
            </Button>
            <Button
              variant={activeSection === 'actions' ? 'contained' : 'outlined'}
              onClick={() => setActiveSection('actions')}
            >
              Actions
            </Button>
          </ButtonGroup>

          {/* Colors Section */}
          {activeSection === 'colors' && (
            <>
              <h4 className="mb-3">Color Scheme</h4>
              <div className="color-grid">
                {Object.entries(theme.colors).map(([key, value]) => {
                  const { hex, alpha } = parseColor(value);
                  return (
                    <div key={key} className="color-item">
                      <label className="form-label">
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                      <div className="color-input-group" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}>
                        <input
                          type="color"
                          value={hex}
                          onChange={(e) => {
                            const newValue = alpha === 1 ? e.target.value : hexToRgba(e.target.value, alpha);
                            handleColorChange(key as keyof ThemeData['colors'], newValue);
                          }}
                          className="color-picker"
                          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => handleColorChange(key as keyof ThemeData['colors'], e.target.value)}
                          className="form-input color-text"
                          placeholder="#000000 or rgba(255,255,255,0.5)"
                          style={{ pointerEvents: 'auto', cursor: 'text' }}
                        />
                      </div>
                      <div className="opacity-controls" style={{ pointerEvents: 'auto', position: 'relative', zIndex: 1 }}>
                        <label className="form-label-sm">Opacity: {Math.round(alpha * 100)}%</label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.01"
                          value={alpha}
                          onChange={(e) => handleOpacityChange(key as keyof ThemeData['colors'], parseFloat(e.target.value))}
                          className="opacity-slider"
                          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Background Section */}
          {activeSection === 'background' && (
            <div>
              <h4 className="mb-3">Background Image</h4>
              {backgroundUrl && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '0.875rem', marginBottom: '8px', color: 'var(--text-secondary)' }}>
                    Current Background:
                  </p>
                  <img
                    src={backgroundUrl}
                    alt="Current background"
                    style={{ maxWidth: '300px', maxHeight: '200px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                    onError={(e) => {
                      console.error('Failed to load background image:', backgroundUrl);
                      e.currentTarget.style.display = 'none';
                    }}
                    onLoad={() => console.log('Background thumbnail loaded successfully')}
                  />
                </div>
              )}
              <label className="btn btn-outlined">
                <span className="material-icons mr-1">upload</span>
                {uploading ? 'Uploading...' : 'Upload Background Image'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBackgroundUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          )}

          {/* Actions Section */}
          {activeSection === 'actions' && (
            <div>
              <h4 className="mb-3">Theme Actions</h4>
              <div className="btn-group" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-contained"
                  onClick={handleSaveTheme}
                  disabled={saving}
                >
                  <span className="material-icons mr-1">
                    {saving ? 'hourglass_empty' : 'save'}
                  </span>
                  {saving ? 'Saving...' : 'Save Theme'}
                </button>
                <button
                  className="btn btn-outlined"
                  onClick={handleResetToDefault}
                >
                  <span className="material-icons mr-1">refresh</span>
                  Reset to Default
                </button>
                <button
                  className="btn btn-outlined"
                  onClick={handleDownloadTheme}
                >
                  <span className="material-icons mr-1">download</span>
                  Download Theme
                </button>
                <label className="btn btn-outlined" style={{ margin: 0 }}>
                  <span className="material-icons mr-1">upload</span>
                  Upload Theme
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadTheme}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};
